import { parseUrl } from 'shared/util/gif-parse'
import { FPS } from '../constants'
import { CreationToolFrame, CreationToolSourceFile } from '../types'
import CreationToolFileType from '../types/file-type'
import getVideoFrames from './get-video-frames'

const preloadImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = document.createElement('img')
        img.onload = () => {
            resolve(img)
        }
        img.onerror = error => {
            reject(error)
        }
        img.src = url
    })

const drawToCanvas = (img: HTMLImageElement | HTMLCanvasElement, maxWidth?: number): HTMLCanvasElement => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    const aspectRatio = img.width / img.height
    canvas.width = maxWidth ? Math.min(img.width, maxWidth) : img.width
    canvas.height = Math.round(canvas.width / aspectRatio)
    context?.drawImage(img, 0, 0, canvas.width, canvas.height)
    return canvas
}

/**
 * Processes any file or url and returns the file's canvas frames
 * @param sourceFile
 * @param length
 * @param maxWidth - adding this field will resize the frames relative to the width
 */
const getSourceFrames = async (
    sourceFile: CreationToolSourceFile,
    length: number = 0,
    maxWidth?: number
): Promise<CreationToolFrame[]> => {
    const { source, type, frames } = sourceFile

    // already has frames
    if (frames.length > 0) {
        const totalFrames = length || frames.length
        const pickedFrames: CreationToolFrame[] = []
        for (let i = 0; i < totalFrames; i++) {
            const frame = Math.round((i / totalFrames) * frames.length)
            pickedFrames.push(frames[frame])
        }
        return maxWidth ? pickedFrames.map(f => ({ ...f, canvas: drawToCanvas(f.canvas, maxWidth) })) : pickedFrames
    }

    // gif
    if (type === CreationToolFileType.ANIMATED_IMAGE) {
        const fileUrl = typeof source === 'string' ? source : URL.createObjectURL(source)
        const canvases: HTMLCanvasElement[] = await parseUrl(fileUrl, length, FPS)
        if (typeof source !== 'string') {
            URL.revokeObjectURL(fileUrl)
        }
        return canvases.map(canvas => ({ canvas: maxWidth ? drawToCanvas(canvas, maxWidth) : canvas }))
    }

    // video
    if (type === CreationToolFileType.VIDEO) {
        const frames = await getVideoFrames(source, length, FPS, maxWidth)
        return frames
    }

    // still
    if (type === CreationToolFileType.STILL_IMAGE) {
        const fileUrl = typeof source === 'string' ? source : URL.createObjectURL(source)
        const img = await preloadImage(fileUrl || (source as string))
        const canvas = drawToCanvas(img, maxWidth)
        if (typeof source !== 'string') {
            URL.revokeObjectURL(fileUrl)
        }
        return [{ canvas }]
    }

    return []
}

export default getSourceFrames
